import posthog from './posthog';
import { emitLog } from './posthogLogs';

// Centralized API configuration
const getBaseUrl = () => {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  
  // Ensure the URL has a protocol
  if (apiUrl.startsWith('http://') || apiUrl.startsWith('https://')) {
    return apiUrl;
  }
  
  // If no protocol, assume https for production domains
  if (apiUrl.includes('thefastestform.com')) {
    return `https://${apiUrl}`;
  }
  
  // Default to https for non-localhost domains
  return `https://${apiUrl}`;
};

const API_CONFIG = {
  BASE_URL: getBaseUrl(),
  API_PREFIX: '/api',
};

export const API_BASE_URL = `${API_CONFIG.BASE_URL}${API_CONFIG.API_PREFIX}`;

// Debug logging in development
if (process.env.NODE_ENV === 'development') {
  console.log('API Configuration:', {
    REACT_APP_API_URL: process.env.REACT_APP_API_URL,
    BASE_URL: API_CONFIG.BASE_URL,
    API_BASE_URL
  });
}

// Helper function for making authenticated requests
export const apiRequest = async (endpoint, options = {}) => {
  const token = localStorage.getItem('token');

  // Forward PostHog IDs to backend for event correlation
  const posthogHeaders = {};
  if (posthog.__loaded) {
    const distinctId = posthog.get_distinct_id();
    const sessionId = posthog.get_session_id();
    if (distinctId) posthogHeaders['X-POSTHOG-DISTINCT-ID'] = distinctId;
    if (sessionId) posthogHeaders['X-POSTHOG-SESSION-ID'] = sessionId;
  }

  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...posthogHeaders,
      ...options.headers,
    },
    ...options,
  };

  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
  
  const response = await fetch(url, config);
  
  // Check if the request failed due to authentication issues
  if (response.status === 401 || response.status === 403) {
    // Only trigger automatic logout if:
    // 1. User has a token (meaning they were supposedly authenticated)
    // 2. This is NOT a login/register/public endpoint
    // 3. This is a protected endpoint that should require authentication
    const isLoginEndpoint = endpoint.includes('/auth/login') || endpoint.includes('/auth/register');
    const isPublicEndpoint = endpoint.startsWith('/public') || endpoint.includes('/health');
    const hasToken = !!localStorage.getItem('token');
    
    if (hasToken && !isLoginEndpoint && !isPublicEndpoint) {
      // This means the user had a token but it's now invalid/expired
      // Clear localStorage and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('organization');
      
      // Redirect to login page
      window.location.href = '/login';
      
      throw new Error('Authentication expired - redirecting to login');
    }
  }

  // Log failed API requests (exclude 401/403 already handled above)
  if (!response.ok && response.status !== 401 && response.status !== 403) {
    emitLog({
      severityText: response.status >= 500 ? 'error' : 'warn',
      body: `API ${config.method || 'GET'} ${endpoint} failed: ${response.status} ${response.statusText}`,
      attributes: {
        'http.method': config.method || 'GET',
        'http.url': url,
        'http.status_code': response.status,
        'http.status_text': response.statusText,
      },
    });
  }

  return response;
};

export default API_BASE_URL;
